import * as C from "../controls"
import * as P from "../program-state"
import * as Rr from "react-router-dom"
import R from "react"
import {Emit_main} from "../navigation"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faBan, faPlus, faRotateLeft, faUpload} from "@fortawesome/free-solid-svg-icons"
import {Err, Ok, result, unreachable, v1api} from "../program-state"
import {z} from "zod"

type wrap_fields<t> = { [k in keyof t] : (t[k])[] }
type asset_display = {key: string, display : string}
type fbassets = Omit<P.facebook__asset_types_record, "fb_businessmanager">
type liassets = Omit<P.linkedin__asset_types_record, "li_insighttag">
type ttassets = Omit<P.tiktok_assets_record, "tt_business_centers"> & { tt_users : P.tiktok_single_user_response_t }
type xassets = { x_ad_accounts : P.x_ad_account_t, x_pixels : P.x_pixel_t }
type everything_t = Partial<Record<keyof fbassets | keyof liassets | keyof ttassets | keyof xassets, asset_display[]>>
type assets = {
    facebook : wrap_fields<fbassets>,
    linkedin : wrap_fields<liassets>,
    tiktok : wrap_fields<ttassets>,
    x : wrap_fields<xassets>,
    everything : everything_t,
    netid_to_eresid : Map<string, number>,
}
type initial_assets = {
    orig : P.brand,
    fb_pixel : string | undefined,
    fb_instagram : string | undefined,
    fb_adaccount : string | undefined,
    fb_page : string | undefined,
    li_adaccount : string | undefined,
    li_organization : string | undefined,
    tt_ad_accounts : string | undefined,
    tt_identities : string | undefined,
    tt_pixels : string | undefined,
    tt_users : string | undefined,
    x_ad_accounts : string | undefined,
    x_pixels : string | undefined,
}

const sort_by_display = (x : {display : string}, y : {display : string}) : number => x.display.localeCompare(y.display)
const process_group = (netid_to_eresid : Map<string, number>) => <t extends {id : number},>(xs : t[], f : (x : t) => asset_display) : asset_display[] => {
    const ret = xs.map(x => {
        const ad = f(x)
        netid_to_eresid.set(ad.key, x.id)
        return ad
    })
    ret.sort(sort_by_display)
    return ret
}

type idpfx = "fbpa-" | "fbaa-" | "fbig-" | "fbpx-"
    | "lior-" | "liaa-"
    | "ttui-" | "ttid-" | "ttaa-" | "ttpx-"
    | "xaa-" | "xpx-"

const pfxid = (pfx : idpfx, netid : number) : string => { return `${pfx}${netid}` }
const _pfxid = (pfx : idpfx, netid : number | null | undefined) : string | undefined => { if (netid === undefined || netid === null) return undefined; return `${pfx}${netid}` }

const brand_to_initial_assets = (brand : P.brand) : initial_assets => {
    const ret : initial_assets = {
        orig : brand,
        fb_pixel : _pfxid("fbpx-", brand.meta_pixel_id),
        fb_adaccount : _pfxid("fbaa-", brand.ad_account_id),
        fb_instagram : _pfxid("fbig-", brand.instagram_asset_id),
        fb_page : _pfxid("fbpa-", brand.meta_page),
        li_adaccount : _pfxid("liaa-", brand.linkedin_ad_account),
        li_organization : _pfxid("lior-", brand.linkedin_org),
        tt_ad_accounts : _pfxid("ttaa-", brand.tiktok_ad_account_id),
        tt_identities : _pfxid("ttid-", brand.tiktok_identity_id),
        tt_pixels : _pfxid("ttpx-", brand.tiktok_pixel_id),
        tt_users : _pfxid("ttui-", brand.tiktok_user_id),
        x_ad_accounts : _pfxid("xaa-", brand.x_ad_account_id),
        x_pixels : _pfxid("xpx-", brand.x_pixel_id),
    }
    return ret
}

const fetch_brand_information = async (sem : P.semaphore, brand_id : brand_id_t, token : P.authn_token) : Promise<result<initial_assets | null, undefined>> => {
    switch (brand_id.tag) {
    case "edit-brand":
        const {brand_dbid, prebrand : prepopulated_brand} = brand_id
        let brand : P.brand
        if (prepopulated_brand !== null) {
            brand = prepopulated_brand
        } else {
            const resp = await P.with_semaphore(sem)(() => P.issue_json_get(v1api`brands/${brand_dbid}`, P.parse_brand, token))
            const [ok, r] = resp
            if (!ok) return [Err, undefined]
            brand = r
        }
        const ret = brand_to_initial_assets(brand)
        return [Ok, ret]
    case "create-brand":
        return [Ok, null]
    default: return unreachable(brand_id)
    }
}

const fetch_all_linked_assets = async (sem : P.semaphore, nets : P.connected_network[], token : P.authn_token) : Promise<result<assets, undefined>> => {
    let fb : P.connected_network | null = null
    let li : P.connected_network | null = null
    let tt : P.connected_network | null = null
    let x : P.connected_network | null = null
    for (const net of nets) {
        switch (net.network_tag) {
        case "nw-facebook": if (fb === null) { fb = net } break
        case "nw-linkedin": if (li === null) { li = net } break
        case "nw-tiktok": if (tt === null) { tt = net } break
        case "nw-x": if (x === null) { x = net } break
        }
    }
    let assets : assets = {
        facebook : {
            fb_pixel : [],
            fb_instagram : [],
            fb_adaccount : [],
            fb_page : [],
        },
        linkedin : {
            li_adaccount : [],
            li_organization : [],
        },
        tiktok : {
            tt_ad_accounts : [],
            tt_identities : [],
            tt_pixels : [],
            tt_users: [],
        },
        x : {
            x_ad_accounts : [],
            x_pixels : [],
        },
        everything : {},
        netid_to_eresid : new Map<string, number>(),
    }
    const pg = process_group(assets.netid_to_eresid)
    let error : {} | null = null
    void await Promise.all([
        (async () => {
            if (fb !== null) {
                const {fb_businessmanager : _unused, ...fbr} = P.facebook__requests_for_all_assets(fb.eres_dbid, token)
                const r = await P.all_with_limiter_rec(sem, fbr)
                const [pok, fb_page] = r.fb_page
                const [aok, fb_adaccount] = r.fb_adaccount
                const [iok, fb_instagram] = r.fb_instagram
                const [pixok, fb_pixel] = r.fb_pixel
                if (pok && aok && iok && pixok) {
                    assets.facebook = {fb_page, fb_adaccount, fb_instagram, fb_pixel}
                    assets.everything.fb_page = pg(fb_page, p => ({key : pfxid("fbpa-", p.id), display : p.name}))
                    assets.everything.fb_adaccount = pg(fb_adaccount, p => ({key : pfxid("fbaa-", p.id), display : p.name}))
                    assets.everything.fb_instagram = pg(fb_instagram, p => ({key : pfxid("fbig-", p.id), display : p.username}))
                    assets.everything.fb_pixel = pg(fb_pixel, p => ({key : pfxid("fbpx-", p.id), display : p.name}))
                } else {
                    error = {}
                }
            }
        })(),
        (async () => {
            if (li !== null) {
                const {li_insighttag : _unused, ...lir} = P.linkedin__requests_for_all_assets(li.eres_dbid, token)
                const r = await P.all_with_limiter_rec(sem, lir)
                const [aok, li_adaccount] = r.li_adaccount
                const [ook, li_organization] = r.li_organization
                if (aok && ook) {
                    assets.linkedin = {li_organization, li_adaccount}
                    assets.everything.li_organization = pg(li_organization, x => ({key : pfxid("lior-", x.id), display : x.name ?? `<organization ID ${x.linkedin_id}>`}))
                    assets.everything.li_adaccount = pg(li_adaccount, x => ({key : pfxid("liaa-", x.id), display : x.name}))
                } else {
                    error = {}
                }
            }
        })(),
        (async () => {
            if (tt !== null) {
                const _ttr = {
                    ...P.tiktok__requests_for_all_assets(tt.eres_dbid, token),
                    tt_users : () => P.issue_json_get(P.v1api`social/tiktok/users`, P.tiktok_user_response, token)
                }
                const {tt_business_centers : _unused, ...ttr} = _ttr
                const r = await P.all_with_limiter_rec(sem, ttr)
                const {tt_ad_accounts : a, tt_identities : i, tt_pixels : p, tt_users : u} = r
                const [aok, tt_ad_accounts] = a
                const [iok, tt_identities] = i
                const [pok, tt_pixels] = p
                const [uok, tt_users] = u
                if (aok && iok && pok && uok) {
                    assets.tiktok = {tt_ad_accounts, tt_identities, tt_pixels, tt_users}
                    assets.everything.tt_ad_accounts = pg(tt_ad_accounts, x => ({key : pfxid("ttaa-", x.id), display : x.name}))
                    assets.everything.tt_identities = pg(tt_identities, x => ({key : pfxid("ttid-", x.id), display : x.display_name}))
                    assets.everything.tt_pixels = pg(tt_pixels, x => ({key : pfxid("ttpx-", x.id), display : x.pixel_name}))
                    assets.everything.tt_users = pg(tt_users, x => ({key : pfxid("ttui-", x.id), display : x.displayname}))
                } else {
                    error = {}
                }
            }
        })(),
        (async () => {
            if (x !== null) {
                const ad_accounts_task = () => P.issue_json_get(P.v1api`social/x/${x.eres_dbid}/ad-accounts`, P.x_ad_accounts_schema, token)
                const pixels_task = () => P.issue_json_get(P.v1api`social/x/${x.eres_dbid}/pixels`, P.x_pixels_schema, token)
                const r = await P.all_with_limiter_rec(sem, {
                    x_ad_accounts : ad_accounts_task,
                    x_pixels : pixels_task,
                })
                const [aok, x_ad_accounts] = r.x_ad_accounts
                const [pok, x_pixels] = r.x_pixels
                if (aok && pok) {
                    assets.x = {x_ad_accounts, x_pixels}
                    assets.everything.x_ad_accounts = pg(x_ad_accounts, x => ({key : pfxid("xaa-", x.id), display : x.name}))
                    assets.everything.x_pixels = pg(x_pixels, x => ({key : pfxid("xpx-", x.id), display : x.name}))
                } else {
                    error = {}
                }
            }
        })(),
    ])
    if (error !== null) return [Err, undefined]
    return [Ok, assets]
}

const empty : asset_display[] = []
type get_field_t = (a : assets | null) => asset_display[]
const gf = (field : keyof everything_t) => (a : assets | null) => a?.everything[field] ?? empty

type fieldshape = {id : string, kind : "nofield"}
    | {id : string, src : get_field_t, dst : keyof Partial<P.parse_brand_create_t>, kind : "eresid"}
    | {id : string, dst : keyof Partial<P.parse_brand_create_t>, kind : "str", required? : boolean}
type fields_t = Record<string, fieldshape>
const fields = {
    cbutton      : {id : "f-cancel", kind : "nofield"},
    sbutton      : {id : "f-save"  , kind : "nofield"},
    yassetname   : {id : "eres-yourasset-name"  , dst : "name", kind : "str", required : true},
    yasseturl    : {id : "eres-yourasset-weburl", dst : "url" , kind : "str"},
    fb_page      : {id : "eres-fb-page"         , src : gf("fb_page")             , dst : "meta_page",            kind : "eresid"},
    fb_adaccount : {id : "eres-fb-adaccount"    , src : gf("fb_adaccount")        , dst : "ad_account_id",        kind : "eresid"},
    fb_pixel     : {id : "eres-fb-pixel"        , src : gf("fb_pixel")            , dst : "meta_pixel_id",        kind : "eresid"},
    fb_ig        : {id : "eres-fb-ig"           , src : gf("fb_instagram")        , dst : "instagram_asset_id",   kind : "eresid"},
    li_org       : {id : "eres-li-org"          , src : gf("li_organization")     , dst : "linkedin_org",         kind : "eresid"},
    li_adaccount : {id : "eres-li-adaccount"    , src : gf("li_adaccount")        , dst : "linkedin_ad_account",  kind : "eresid"},
    tt_user      : {id : "eres-tt-user"         , src : gf("tt_users")            , dst : "tiktok_user_id",       kind : "eresid"},
    tt_identity  : {id : "eres-tt-identity"     , src : gf("tt_identities")       , dst : "tiktok_identity_id",   kind : "eresid"},
    tt_adaccount : {id : "eres-tt-adaccount"    , src : gf("tt_ad_accounts")      , dst : "tiktok_ad_account_id", kind : "eresid"},
    tt_pixel     : {id : "eres-tt-pixel"        , src : gf("tt_pixels")           , dst : "tiktok_pixel_id",      kind : "eresid"},
    x_adaccount  : {id : "eres-x-adaccount"     , src : gf("x_ad_accounts")       , dst : "x_ad_account_id",      kind : "eresid"},
    x_pixel      : {id : "eres-x-pixel"         , src : gf("x_pixels")            , dst : "x_pixel_id",           kind : "eresid"},
} satisfies fields_t
const ix_by_html_id : Map<string, fieldshape> = new Map()
const ix_by_upload_field : Map<string, fieldshape> = new Map()
for (const k in fields) {
    const v = (fields as fields_t)[k]
    ix_by_html_id.set(v.id, v)
    if (v.kind === "eresid" || v.kind === "str") ix_by_upload_field.set(v.dst, v)
    else if (v.kind === "nofield") continue
    else unreachable(v)
}

const Formlabel = R.memo(({field, label} : {field : {id : string}, label : string}) => {
    return (<div className="tw-self-start tw-h-9">
        <C.Eres_form_label htmlFor={field.id} className="tw-h-9 tw-table-cell tw-align-middle">{label}</C.Eres_form_label>
    </div>)
})

type cross_nav_state = { __brand_cross_nav_state : boolean, initial_assets : P.brand, assets : assets }

const submit_new_brand = async (create_or_update : brand_id_t, upload_payload : Partial<P.parse_brand_create_t>, init_state : brands_page_loaded, set_state : (st : brands_page_saving | brands_page_loaded) => void, token : P.authn_token, nav : Rr.NavigateFunction, crapholder : P.crapholder) => {
    set_state({tag : "bps-saving", initial_assets : init_state.initial_assets, assets : init_state.assets})
    let errmsg : string
    let warnmsg : string
    let upload_action : (body : Partial<P.parse_brand_create_t>) => Promise<result<P.brand, P.possible_http_errors>>
    let success_nav_action : (response : P.brand) => void
    switch (create_or_update.tag) {
    case "create-brand":
        errmsg = "There was an unexpected error when attempting to save your new brand. Try again in a few minutes."
        warnmsg = "brand creation succeeded with an unexpected response"
        success_nav_action = (_parsed : P.brand) => { nav("/brands/") }
        upload_action = (body : Partial<P.parse_brand_create_t>) => P.issue_json_post(v1api`brands`, body, z.any(), token)
        break
    case "edit-brand":
        errmsg = "There was an unexpected error when attempting to save your new brand. Try again in a few minutes."
        warnmsg = "brand update succeeded with an unexpected response"
        success_nav_action = (_parsed : P.brand) => { nav("/brands/") }
        upload_action = (body : Partial<P.parse_brand_create_t>) => P.issue_json_put(v1api`brands/${create_or_update.brand_dbid}`, body, z.any(), token)
        break
    default: return unreachable(create_or_update)
    }
    const [ok, resp] = await upload_action(upload_payload)
    if (!ok) {
        switch (true) {
        case resp instanceof P.http_error:
            switch (resp.resp.status) {
            case 403:
                try {
                    const errbody = await resp.resp.json()
                    if (errbody.detail?.message?.includes('Brand limit reached')) {
                        errmsg = `${errbody.detail.message} <a href="/plans" class="tw-text-big-stone-rlydark tw-underline tw-font-semibold">Upgrade your plan</a> to add more brands.`
                    } else {
                        switch (typeof errbody.detail) {
                        case "object": if (errbody.detail.__root__) errmsg = `Server error: ${errbody.detail.__root__}`; break
                        case "string": errmsg = `Server error: ${errbody.detail}`; break
                        }
                    }
                } catch (e) { void e }
                break
            case 422: case 500:
                try {
                    const errbody = await resp.resp.json()
                    switch (typeof errbody.detail) {
                    case "object": if (errbody.detail.__root__) errmsg = `Server error: ${errbody.detail.__root__}`; break
                    case "string": errmsg = `Server error: ${errbody.detail}`; break
                    }
                } catch (e) { void e }
                break
            }
            break
        case resp instanceof TypeError: // fallthrough
        case resp instanceof SyntaxError: // fallthrough
        case resp instanceof z.ZodError: break
        default: return unreachable(resp)
        }
        console.error("brand create error:", resp)
        set_state({tag : "bps-loaded", initial_assets : init_state.initial_assets, assets : init_state.assets, last_error : errmsg})
        return
    }
    const [parseok, parsed] = P.zodparse(P.parse_brand, resp)
    if (!parseok) {
        console.warn(warnmsg, resp)
        nav("/brands")
        return
    }
    success_nav_action(parsed)
    return
}

type brands_page_loading = {tag : "bps-loading", readonly initial_assets : P.brand | null}
type brands_page_loaded = {tag : "bps-loaded", readonly initial_assets : initial_assets | null, assets : assets, last_error : string | null}
type brands_page_saving = {tag : "bps-saving", readonly initial_assets : initial_assets | null, assets : assets}
type brands_page_state = brands_page_loading | brands_page_loaded | brands_page_saving

export type brand_id_t = {tag : "create-brand"} | {tag : "edit-brand", brand_dbid : P.eres_brand_dbid, prebrand : P.brand | null, preassets : assets | null}
type brand_editor_props = {brand_id : brand_id_t}
const _Brand_editor = ({brand_id} : brand_editor_props) => {
    const nav = Rr.useNavigate()
    const [connected_networks, ps, us] = P.use_connected_networks()
    P.run_once_on_component_creation(() => void P.poll_connected_networks(ps, us))
    let upper_right : R.ReactElement | string | null = "Loading..."
    let disabled = true
    let global_errors : string | null = null
    const fetch_task = R.useRef<any>(null)
    const [state, set_state] = R.useState(() : brands_page_state => {
        let b : P.brand | null
        {
            switch (brand_id.tag) {
            case "create-brand": b = null; break;
            case "edit-brand":
                const {prebrand, preassets} = brand_id;
                if (prebrand !== null && preassets !== null) {
                    const initial_assets = brand_to_initial_assets(prebrand)
                    return {tag : "bps-loaded", initial_assets, assets : preassets, last_error : null}
                }
                b = prebrand;
                break;
            default: return unreachable(brand_id)
            }
        }
        return {tag : "bps-loading", initial_assets : b}
    })
    const validation_errors = R.useRef(new Set<string>())
    const _frd = R.useRef(0)
    const [, _force_redraw] = R.useState(0)
    const force_redraw = R.useCallback(() => { ++_frd.current; _force_redraw(_frd.current) }, [_force_redraw])
    const handle_submit = R.useCallback((e : R.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        let assets : assets
        switch (state.tag) {
        case "bps-loading": return
        case "bps-loaded": assets = state.assets; break
        case "bps-saving": return
        default: return unreachable(state)
        }
        const verrs = validation_errors.current
        verrs.clear()
        const elts = e.currentTarget
        let upload_payload_partial : Partial<P.parse_brand_create_t> = {}
        const len = e.currentTarget.length
        for (let i = 1; i < len; ++i) {
            const elt = elts[i]
            const eid = elt.id
            const field_descr = ix_by_html_id.get(eid)
            if (field_descr === undefined) throw new Error(`invariant violation ${eid}`)
            if (field_descr.kind === "nofield") continue
            else if (field_descr.kind === "eresid") {
                let fval : string | undefined
                if (C.is_combobox(elt)) {
                    fval = elt.__eres_combokey
                }
                if (fval === undefined) continue
                const eres_id = assets.netid_to_eresid.get(fval)
                if (eres_id === undefined) throw new Error(`invariant violation ${eid} ${fval}`)
                ;
                (upload_payload_partial[field_descr.dst] as any) = eres_id
            } else if (field_descr.kind === "str") {
                let fval : string | undefined
                if (elt instanceof HTMLInputElement) {
                    const val = elt.value
                    if (val === "") {
                        if (field_descr.required) verrs.add(eid)
                        continue
                    }
                    fval = elt.value
                }
                if (fval === undefined) continue
                (upload_payload_partial[field_descr.dst] as any) = fval
            } else return unreachable(field_descr)
        }
        console.log("partial", upload_payload_partial)
        if (verrs.size > 0) {
            // Redraw with validation errors.
            force_redraw()
        } else {
            void submit_new_brand(brand_id, upload_payload_partial, state, set_state, us.token, nav, us.crapholder)
        }
    }, [state, us])
    let assets : assets | null = null
    let ia : initial_assets | null
    const gen_upper_right = () => {
        switch (brand_id.tag) {
        case "create-brand":
            upper_right = (<C.Eres_iconbutton id="f-save" icon={faPlus} type="submit" disabled={disabled}>Add Brand</C.Eres_iconbutton>)
            return
        case "edit-brand":
            upper_right = (<div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-1">
                <C.Eres_iconbutton id="f-cancel" icon={faRotateLeft} type="reset" disabled={disabled}>Revert Changes</C.Eres_iconbutton>
                <C.Eres_iconbutton id="f-save" icon={faUpload} type="submit" disabled={disabled}>Update Brand</C.Eres_iconbutton>
            </div>)
            return
        default: return unreachable(brand_id)
        }
    }
    if (connected_networks !== undefined) {
        switch (state.tag) {
        case "bps-loading":
            if (fetch_task.current === null) {
                fetch_task.current = {}
                const sem = P.make_semaphore(5)
                void (async () => {
                    const [initial_assets, assets] = await Promise.all([
                        fetch_brand_information(sem, brand_id, us.token),
                        fetch_all_linked_assets(sem, connected_networks, us.token),
                    ])
                    const [iok, i] = initial_assets
                    const [aok, a] = assets
                    if (aok && iok) {
                        const ns : brands_page_state = {tag : "bps-loaded", initial_assets : i, assets : a, last_error : null}
                        set_state(ns)
                    } else {
                        console.error("error fetching assets") // TODO: add a retry button
                    }
                })()
            }
            ia = null
            break
        case "bps-loaded":
            disabled = false
            assets = state.assets
            global_errors = state.last_error
            ia = state.initial_assets
            gen_upper_right()
            break
        case "bps-saving":
            disabled = true
            assets = state.assets
            ia = state.initial_assets
            gen_upper_right()
            break
        default: return unreachable(state)
        }
    } else {
        ia = null
    }
    return (<>
        <form role="form" className="tw-text-sm" onSubmit={handle_submit}>
        <fieldset disabled={disabled}>
        <C.Eres_bare_card className="tw-mb-6">
            <div className="tw-flex tw-items-center">
                <div className="tw-flex-grow">
                <C.Eres_header2xl className="tw-font-semibold">Create a Brand</C.Eres_header2xl>
                <p>A brand is a collection of social media assets you can use as a
                preset during campaign creation.</p>
                </div>
                <div className="tw-flex-shrink-0">
                {upper_right}
                </div>
            </div>
        </C.Eres_bare_card>
        <C.Eres_hline/>
        <div className="tw-mt-3 tw-grid tw-grid-cols-1 lg:tw-grid-cols-[33%,1fr] tw-gap-3">
            {global_errors !== null
                ? <C.Eres_alert className="tw-bg-dangergrad tw-text-white tw-col-span-2 tw-relative">
                    <FontAwesomeIcon icon={faBan} className="tw-w-[14px] tw-h-[14px] tw-me-2 tw-absolute tw-mt-1"/>
                    <p className="tw-relative tw-ps-5" dangerouslySetInnerHTML={{__html : global_errors}}>
                    </p>
                </C.Eres_alert>
                : null}
            <C.Eres_bare_card>
                <C.Eres_headerlg>Your Assets</C.Eres_headerlg>
                <p className="tw-text-sm">These are assets you own.</p>
            </C.Eres_bare_card>
            <C.Eres_card>
            <div className="tw-m-3">
                <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <Formlabel field={fields.yassetname} label="Name"/>
                <div className="tw-w-full">
                    <div className="tw-relative">
                        <C.Eres_line_edit id={fields.yassetname.id} aria-label="name" defaultValue={ia?.orig.name ?? undefined} type="text" className="tw-w-full" style={{backgroundPosition : "right .75rem center", backgroundSize : "1rem 1rem"}}/>
                        {validation_errors.current.has(fields.yassetname.id)
                            ? <div className="tw-bg-no-repeat tw-bg-center tw-bg-eformerror tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-[24px] tw-inline-block"/>
                            : null}
                    </div>
                    {validation_errors.current.has(fields.yassetname.id)
                            ? (<C.Eres_form_error className="tw-mt-0" msg="Please enter a non-empty name."/>)
                            : null}
                </div>
                <Formlabel field={fields.yasseturl} label="Website URL"/>
                <C.Eres_line_edit id={fields.yasseturl.id} aria-label="url" defaultValue={ia?.orig.url ?? undefined} type="text"/>
                </div>
            </div>
            </C.Eres_card>

            <C.Eres_bare_card>
                <C.Eres_headerlg>Facebook Assets</C.Eres_headerlg>
                <p className="tw-text-sm">Select some Facebook assets to use when you create campaigns with this brand.</p>
            </C.Eres_bare_card>
            <C.Eres_card>
            <div className="tw-m-3">
                <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <Formlabel field={fields.fb_adaccount} label="Ad Account"/>
                <C.Eres_combobox id={fields.fb_adaccount.id} onBlur={() => { console.log("combobox fbaa blurred") }} data={fields.fb_adaccount.src(assets)} defaultValue={ia?.fb_adaccount} search_placeholder="Search for an ad account..." empty_selection_text="No ad account selected" unselect_placeholder="Don't use an ad account" no_matches_placeholder="No matching ad accounts"/>
                <Formlabel field={fields.fb_page} label="Page"/>
                <C.Eres_combobox id={fields.fb_page.id} onBlur={() => { console.log("combobox fbpage blurred") }} data={fields.fb_page.src(assets)} defaultValue={ia?.fb_page} search_placeholder="Search for a page..." empty_selection_text="No page selected" unselect_placeholder="Don't use a page" no_matches_placeholder="No matching pages"/>
                <Formlabel field={fields.fb_pixel} label="Pixel"/>
                <C.Eres_combobox id={fields.fb_pixel.id} data={fields.fb_pixel.src(assets)} defaultValue={ia?.fb_pixel} search_placeholder="Search for a pixel..." empty_selection_text="No pixel selected" unselect_placeholder="Don't use a pixel" no_matches_placeholder="No matching pixels"/>
                <Formlabel field={fields.fb_ig} label="Instagram"/>
                <C.Eres_combobox id={fields.fb_ig.id} data={fields.fb_ig.src(assets)} defaultValue={ia?.fb_instagram} search_placeholder="Search for instagram..." empty_selection_text="No instagram selected" unselect_placeholder="Don't use instagram" no_matches_placeholder="No matching instagram"/>
                </div>
            </div>
            </C.Eres_card>

            <C.Eres_bare_card>
                <C.Eres_headerlg>LinkedIn Assets</C.Eres_headerlg>
                <p className="tw-text-sm">Select some LinkedIn assets to use when you create campaigns with this brand.</p>
            </C.Eres_bare_card>
            <C.Eres_card>
            <div className="tw-m-3">
                <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <Formlabel field={fields.li_adaccount} label="Ad Account"/>
                <C.Eres_combobox id={fields.li_adaccount.id} data={fields.li_adaccount.src(assets)} defaultValue={ia?.li_adaccount} search_placeholder="Search for an ad account..." empty_selection_text="No ad account selected" unselect_placeholder="Don't use an ad account" no_matches_placeholder="No matching ad accounts"/>
                <Formlabel field={fields.li_org} label="Organization"/>
                <C.Eres_combobox id={fields.li_org.id} data={fields.li_org.src(assets)} defaultValue={ia?.li_organization} search_placeholder="Search for an organization..." empty_selection_text="No organization selected" unselect_placeholder="Don't use an organization" no_matches_placeholder="No matching organizations"/>
                </div>
            </div>
            </C.Eres_card>

            <C.Eres_bare_card>
                <C.Eres_headerlg>TikTok Assets</C.Eres_headerlg>
                <p className="tw-text-sm">Select some TikTok assets to use when you create campaigns with this brand.</p>
            </C.Eres_bare_card>
            <C.Eres_card>
            <div className="tw-m-3">
                <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <Formlabel field={fields.tt_adaccount} label="Ad Account"/>
                <C.Eres_combobox id={fields.tt_adaccount.id} data={fields.tt_adaccount.src(assets)} defaultValue={ia?.tt_ad_accounts} search_placeholder="Search for an ad account..." empty_selection_text="No ad account selected" unselect_placeholder="Don't use an ad account" no_matches_placeholder="No matching ad accounts"/>
                <Formlabel field={fields.tt_user} label="User"/>
                <C.Eres_combobox id={fields.tt_user.id} data={fields.tt_user.src(assets)} defaultValue={ia?.tt_users} search_placeholder="Search for a user..." empty_selection_text="No user selected" unselect_placeholder="Don't use a user" no_matches_placeholder="No matching users"/>
                <Formlabel field={fields.tt_identity} label="Identity"/>
                <C.Eres_combobox id={fields.tt_identity.id} data={fields.tt_identity.src(assets)} defaultValue={ia?.tt_identities} search_placeholder="Search for an identity..." empty_selection_text="No identity selected" unselect_placeholder="Don't use an identity" no_matches_placeholder="No matching identities"/>
                <Formlabel field={fields.tt_pixel} label="Pixel"/>
                <C.Eres_combobox id={fields.tt_pixel.id} data={fields.tt_pixel.src(assets)} defaultValue={ia?.tt_pixels} search_placeholder="Search for a pixel..." empty_selection_text="No pixel selected" unselect_placeholder="Don't use a pixel" no_matches_placeholder="No matching pixels"/>
                </div>
            </div>
            </C.Eres_card>

            <C.Eres_bare_card>
                <C.Eres_headerlg>X (Twitter) Assets</C.Eres_headerlg>
                <p className="tw-text-sm">Select some X assets to use when you create campaigns with this brand.</p>
            </C.Eres_bare_card>
            <C.Eres_card>
            <div className="tw-m-3">
                <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <Formlabel field={fields.x_adaccount} label="Ad Account"/>
                <C.Eres_combobox id={fields.x_adaccount.id} data={fields.x_adaccount.src(assets)} defaultValue={ia?.x_ad_accounts} search_placeholder="Search for an ad account..." empty_selection_text="No ad account selected" unselect_placeholder="Don't use an ad account" no_matches_placeholder="No matching ad accounts"/>
                <Formlabel field={fields.x_pixel} label="Web Event Tag (Pixel)"/>
                <C.Eres_combobox id={fields.x_pixel.id} data={fields.x_pixel.src(assets)} defaultValue={ia?.x_pixels} search_placeholder="Search for a pixel..." empty_selection_text="No pixel selected" unselect_placeholder="Don't use a pixel" no_matches_placeholder="No matching pixels"/>
                </div>
            </div>
            </C.Eres_card>
        </div>
        </fieldset>
        </form>
    </>)
}

export const Brand_editor = R.memo(_Brand_editor)

export const use_hidden_params = (us : P.user_state) : cross_nav_state | null => {
    const loc = Rr.useLocation() as Rr.Location & { __eres_brndp? : cross_nav_state }
    if (loc.__eres_brndp !== undefined) return loc.__eres_brndp
    else {
        const st = loc.state
        if (typeof st === "string") {
            const val = us.crapholder.get(st)
            if (val !== undefined) {
                const ret = val as cross_nav_state
                loc.__eres_brndp = ret
                return ret
            }
        }
    }
    return null
}

export function Page_edit_brand() {
    const {brand_id} = Rr.useParams()
    const [ps, us] = P.use_authenticated_program_state()
    void ps
    const hidden_params = use_hidden_params(us)
    let page : R.ReactElement
    if (brand_id === undefined) page = <h1>b12c8e608d8cffb4b7ff87e15adadceb missing dbid</h1>
    else {
        const parsed = P.parse_eres_brand_dbid(brand_id)
        const [ok, parsed_brand_dbid] = parsed
        if (ok) page = (<Brand_editor key={brand_id} brand_id={{tag : "edit-brand", brand_dbid : parsed_brand_dbid, prebrand : hidden_params?.initial_assets ?? null, preassets : hidden_params?.assets ?? null}}/>)
        else page = (<h1>a5200d0b990b34f294b58fff4aea752e invalid dbid</h1>)
    }
    return (<Emit_main page_id="PAGE-brands">{page}</Emit_main>)
}
